export const AUDIT_PREVIEW_LIMIT = 10;

export const AUDIT_FIELD_LABELS = {
  ppm_next_due_date: 'PPM next due date',
  calibration_next_due_date: 'Calibration next due date',
  compliance_next_due_date: 'Compliance next due date',
  calibration_required: 'Calibration required',
  compliance_required: 'Compliance required',
  device_identifier: 'Device ID',
  device_status: 'Device status',
  department: 'Department',
  location: 'Location',
  product_cost: 'Product cost',
  service_contract_type: 'Service contract type',
  service_contract_cost: 'Service contract cost',
  contract_expiry: 'Contract expiry',
  warranty_expiry: 'Warranty expiry',
  installation_date: 'Installation date',
  purchase_date: 'Purchase date',
  location_ids: 'Locations',
  device_name_ids: 'Device names',
  applies_to_all_locations: 'All locations',
  applies_to_all_device_names: 'All device names',
};

export const formatAuditFieldLabel = (fieldName) => {
  if (!fieldName) return 'Unknown field';
  if (AUDIT_FIELD_LABELS[fieldName]) return AUDIT_FIELD_LABELS[fieldName];
  return String(fieldName).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const COMPLIANCE_IMPACTING_MARKERS = [
  'ppm_next_due_date',
  'calibration_next_due_date',
  'compliance_next_due_date',
  'ppm',
  'calibration',
  'compliance_required',
  'calibration_required',
];

export const isComplianceImpactingEvent = (ev) => {
  const fields = (ev?.changed_fields || []).join(',').toLowerCase();
  return COMPLIANCE_IMPACTING_MARKERS.some((marker) => fields.includes(marker));
};

export const matchesAuditActionFilter = (ev, filter) => {
  if (filter === 'all') return true;
  const action = (ev?.action || '').toLowerCase();
  if (filter === 'new') return action.includes('new');
  if (filter === 'modified') return action.includes('modified');
  if (filter === 'deleted') return action.includes('deleted');
  if (filter === 'transfer') return action.includes('transfer') || ev?.event_type === 'transfer';
  if (filter === 'compliance') return isComplianceImpactingEvent(ev);
  return true;
};

export const filterAuditEvents = (events, groupFilter, quickFilter, groupField = 'department') => (
  (events || [])
    .filter((ev) => {
      if (!groupFilter) return true;
      if (groupField === 'location') {
        const action = (ev.action || '').toLowerCase();
        if (action.includes('transfer') || ev.event_type === 'transfer') {
          return ev.from_location === groupFilter || ev.to_location === groupFilter || ev.location === groupFilter;
        }
        return ev.location === groupFilter;
      }
      return ev.department === groupFilter;
    })
    .filter((ev) => matchesAuditActionFilter(ev, quickFilter))
);

export const locationHasComplianceImpactingEvents = (events, locationName) => (
  (events || []).some((ev) => ev.location === locationName && isComplianceImpactingEvent(ev))
);

export const getActionColor = (action) => {
  if (!action) return '#999';
  if (action.includes('New')) return '#4caf50';
  if (action.includes('Modified')) return '#1976d2';
  if (action.includes('Deleted')) return '#d32f2f';
  return '#999';
};

export const getComplianceRateValue = (rate) => {
  if (rate == null) return null;
  if (typeof rate === 'object') {
    if (rate.value === null) return null;
    return rate.value ?? null;
  }
  return rate;
};

export const getComplianceRateReason = (rate) => {
  if (rate && typeof rate === 'object' && rate.reason) return rate.reason;
  return 'No data available';
};

export const getComplianceCardClass = (rate) => {
  const value = getComplianceRateValue(rate);
  if (value === null) return 'inactive';
  if (value >= 80) return 'active';
  if (value >= 60) return 'deactivated';
  return 'open';
};

export const shouldElevateComplianceCard = (rate) => {
  const value = getComplianceRateValue(rate);
  return value !== null && value < 60;
};

export const getComplianceRateBadgeClass = (rate) => {
  const value = getComplianceRateValue(rate);
  if (value === null) return 'cs-rate-badge--na';
  if (value >= 80) return 'cs-rate-badge--ok';
  if (value >= 60) return 'cs-rate-badge--warn';
  return 'cs-rate-badge--bad';
};

export const deptHasComplianceImpactingEvents = (events, deptName) => (
  (events || [])
    .filter((ev) => ev.department === deptName)
    .some(isComplianceImpactingEvent)
);
